/* eslint-disable react/prop-types */
import {
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  CircularProgress,
  Box,
  useMediaQuery,
} from "@mui/material";
import styled from "@emotion/styled";
import { keyframes } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import LoadingOverlay from "./LoadingOverlay";
import { Refresh } from "@mui/icons-material";
import { CustomIconButton } from "./CustomIconButton";
import { getCssVariableHandler } from "../../utils/methods/getCssVariables";
import { closeAlertDialog } from "../../toolkit/alertDialog/alertDialogSlice";
import theme from "../../theme";

const bounceAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  25% {
    transform: scale(1.1);
  }
  50% {
    transform: scale(0.9);
  }
  75% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const CustomDialogComponent = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    animation: `${bounceAnimation} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
  },
  "& .MuiDialog-paper": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.secondary.main}`,
  },
}));

const CustomButtonComponent = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: theme.palette.secondary.main,
  borderRadius: "8px",
  border: "none",
  boxShadow:
    " 0px 30px 80px 0px rgba(255, 0, 255, 0.30), 0px 0px 20px 0px rgba(0, 0, 0, 0.60) inset",
  "&:hover": {
    boxShadow:
      " 0px 30px 80px 0px rgba(255, 0, 255, 0.30), 0px 0px 20px 0px rgba(0, 0, 0, 0.60) inset",
  },
}));

const CustomCancelButtonComponent = styled(Button)(() => ({
  color: "white",
  backgroundColor: "black",
  borderRadius: "8px",
  border: "none",
  boxShadow: "  0px 0px 20px 0px gray inset",
  "&:hover": {
    color: "white",
    backgroundColor: "black",
    borderRadius: "8px",
    border: "none",
    boxShadow: "  0px 0px 10px 0px gray inset",
  },
}));

export const AlertDialog = () => {
  const dispatch = useDispatch();
  const {
    open,
    isLoading,
    title,
    message,
    onOk,
    okLabel,
    onClose,
    closeLabel,
    styles,
    avoidCloseOnOK = false,
    isDangerouslySetHtml,
  } = useSelector((state) => state.alertDialog);

  function onOkHandler() {
    if (onOk) {
      onOk();
    }

    if (!avoidCloseOnOK) {
      dispatch(closeAlertDialog());
    }
  }

  function onCloseHandler() {
    if (onClose) {
      onClose();
    }
    dispatch(closeAlertDialog());
  }

  if (!open) {
    return null;
  }

  return (
    <CustomDialogComponent
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose(event, reason);
        }
      }}
      fullWidth
      maxWidth="sm"
      className="custom-scroll-design"
      sx={{
        "& .MuiPaper-root": {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h4"
          color="primary"
          sx={{
            textAlign: styles?.message?.textAlign || "left",
          }}
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          component="div"
          color="text.primary"
          sx={{
            textAlign: styles?.message?.textAlign || "left",
          }}
        >
          {isDangerouslySetHtml ? (
            <span dangerouslySetInnerHTML={{ __html: message }} />
          ) : (
            <span
              style={{
                whiteSpace: "pre-line",
              }}
            >
              {message}
            </span>
          )}
          {/* {message} */}
        </Typography>
      </DialogContent>
      <DialogActions>
        <CustomCancelButtonComponent onClick={onCloseHandler}>
          {closeLabel}
        </CustomCancelButtonComponent>
        {onOk && okLabel && (
          <CustomButtonComponent onClick={onOkHandler}>
            {isLoading ? <CircularProgress 
              size={22} 
              sx={{
                color: theme.palette.background.paper
              }}
            /> : okLabel}
          </CustomButtonComponent>
        )}
      </DialogActions>
    </CustomDialogComponent>
  );
};

export const AlertDialogForSendOpportunity = ({
  open,
  isLoading,
  title,
  onOk,
  okLabel,
  onClose,
  closeLabel = "Close",
  styles,
  handleClose,
  message,
  onRefresh,
}) => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  function onOkHandler() {
    if (onOk) {
      onOk();
    }
  }

  function onCloseHandler() {
    if (onClose) {
      onClose();
    }
    handleClose();
  }

  if (!open) {
    return null;
  }

  return (
    <CustomDialogComponent
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose(event, reason);
        }
      }}
      fullWidth
      maxWidth="sm"
      className="custom-scroll-design"
      sx={{
        display: "flex",
        alignItems: "center",
        position: "absolute",
        justifyContent: isSmallScreen ? "flex-start" : "center",
        "& .MuiPaper-root": {
          maxHeight: "80vh",
          maxWidth: "60vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      {isLoading ? <LoadingOverlay open={isLoading} /> : null}
      <DialogTitle
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: onRefresh ? "space-between" : "flex-start",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          color="secondary"
          sx={{
            textAlign: styles?.message?.textAlign || "left",
          }}
        >
          {title}
        </Typography>
        {onRefresh && (
          <CustomIconButton
            sx={{
              backgroundColor: getCssVariableHandler({
                variableName: "--primary-pink-hex",
              }),
              "&:hover": {
                backgroundColor: getCssVariableHandler({
                  variableName: "--primary-pink-hex",
                }),
              },
            }}
            onClick={onRefresh}
          >
            <Refresh sx={{ fontSize: "1.25rem" }} />
          </CustomIconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            maxHeight: "70vh",
            width: "100%",
          }}
          className="custom-scroll-design"
        >
          {message}
        </Box>
      </DialogContent>
      <DialogActions>
        <CustomCancelButtonComponent onClick={onCloseHandler}>
          {closeLabel}
        </CustomCancelButtonComponent>
        {onOk && okLabel && (
          <CustomButtonComponent onClick={onOkHandler}>
            {okLabel}
          </CustomButtonComponent>
        )}
      </DialogActions>
    </CustomDialogComponent>
  );
};

export {
  CustomDialogComponent,
  CustomCancelButtonComponent,
  CustomButtonComponent,
};
